import React from "react";
import { Button, Divider, Tag } from "antd";
import { CalendarOutlined, EnvironmentOutlined, TeamOutlined } from "@ant-design/icons";
import { CompactTable } from "../../common/components/CompactTable";
import { TextWithIconPrefix } from "../../common/components/TextWithIconPrefix";
import { GoalieSelector } from "../../common/components/GoalieSelector";
import { Attendance, Game } from "../../common/interfaces";
import { useGet, usePost } from "../../common/hooks";
import { formatISODate } from "../../common/utils";
import { useParams } from "react-router";
import Spinner from "../../common/components/Spinner";

interface GoalieForGame {
  id: number;
  name: string;
  availability: Attendance;
  contact: string;
  assignedHome: boolean;
  assignedAway: boolean;
}

function sortGoalies(goalies: GoalieForGame[]): GoalieForGame[] {
  const order = (g: GoalieForGame) => {
    if (g.assignedHome || g.assignedAway) return 0;
    if (g.availability === Attendance.IN) return 1;
    if (g.availability === Attendance.UNRESPONDED) return 2;
    return 3;
  };
  return [...goalies].sort((a, b) => order(a) - order(b));
}

export function GoalieAssign() {
  const { gameId } = useParams();
  const { data: game, isLoading: gameLoading } = useGet<Game>(`/api/games?gameId=${gameId}`);
  const goaliesReq = useGet<GoalieForGame[]>(
    gameId ? `/api/goaliesForGame?gameId=${gameId}` : undefined
  );
  const assignReq = usePost({ uri: "/api/submitRoster", deps: [goaliesReq] });

  if (gameLoading || goaliesReq.isLoading || !game) {
    return <Spinner size="large" />;
  }

  const sorted = sortGoalies(goaliesReq.data || []);

  const assign = (row: GoalieForGame | null, teamId: number, originalGoalieName: string | undefined) =>
    assignReq.send({
      goalie: row ? row.name : "",
      goalieId: row ? row.id : undefined,
      gameId,
      teamId,
      originalGoalieName
    });

  const columns = [
    {
      key: "name",
      title: "Goalie",
      render: (_: any, row: GoalieForGame) => (
        <span>
          {row.name}
          {(row.assignedHome || row.assignedAway) && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {row.assignedHome ? "Home" : "Away"}
            </Tag>
          )}
        </span>
      )
    },
    {
      key: "availability",
      title: "Availability",
      render: (_: any, row: GoalieForGame) => {
        switch (row.availability) {
          case Attendance.IN:          return <Tag color="cyan">Available</Tag>;
          case Attendance.OUT:         return <Tag color="red">Unavailable</Tag>;
          case Attendance.UNRESPONDED: return "";
        }
      }
    },
    {
      key: "contact",
      title: "Contact",
      dataIndex: "contact"
    },
    {
      key: "actions",
      title: "",
      render: (_: any, row: GoalieForGame) => {
        const isAssigned = row.assignedHome || row.assignedAway;
        return (
          <span style={{ display: "flex", gap: 8 }}>
            <Button
              size="small"
              danger={isAssigned && row.assignedHome}
              disabled={isAssigned && !row.assignedHome}
              loading={assignReq.isLoading}
              onClick={() => assign(
                row.assignedHome ? null : row,
                game.team1Id,
                row.assignedHome ? row.name : game.team1GoalieName
              )}
            >
              {isAssigned && row.assignedHome ? "Unassign - Home" : "Assign - Home"}
            </Button>
            <Button
              size="small"
              danger={isAssigned && row.assignedAway}
              disabled={isAssigned && !row.assignedAway}
              loading={assignReq.isLoading}
              onClick={() => assign(
                row.assignedAway ? null : row,
                game.team2Id,
                row.assignedAway ? row.name : game.team2GoalieName
              )}
            >
              {isAssigned && row.assignedAway ? "Unassign - Away" : "Assign - Away"}
            </Button>
          </span>
        );
      }
    }
  ];

  return (
    <>
      <div style={{ paddingBottom: 10 }}>
        <TextWithIconPrefix icon={<TeamOutlined />} text={`${game.team1Name} (Home) vs ${game.team2Name} (Away)`} />
        <TextWithIconPrefix icon={<CalendarOutlined />} text={formatISODate(game.date)} />
        <TextWithIconPrefix icon={<EnvironmentOutlined />} text={game.rinkName} />
      </div>
      <Divider />
      <CompactTable data={sorted} columns={columns} />
      <Divider>Override Assignment</Divider>
      <div style={{ display: "flex", gap: 24 }}>
        <div>
          <div style={{ marginBottom: 4 }}>{game.team1Name} (Home)</div>
          <GoalieSelector gameId={String(game.id)} teamId={String(game.team1Id)} showSubLabel />
        </div>
        <div>
          <div style={{ marginBottom: 4 }}>{game.team2Name} (Away)</div>
          <GoalieSelector gameId={String(game.id)} teamId={String(game.team2Id)} showSubLabel />
        </div>
      </div>
    </>
  );
}
